import { forwardRef } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { LoaderCircle } from 'lucide-react';
import { cn } from '../lib/cn';

const button = cva(
  'inline-flex items-center justify-center gap-2 font-semibold tracking-[-0.01em] transition-colors duration-[var(--duration-micro)] ease-wc cursor-pointer active:scale-[0.97] disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        primary: 'bg-wc-blue text-onbrand hover:bg-wc-blue-80',
        secondary: 'bg-selected text-accent border border-wc-blue-20 hover:bg-wc-blue-20',
        dark: 'bg-wc-dark text-white hover:bg-wc-dark-80 dark:bg-raised dark:hover:bg-hover',
        ghost: 'bg-transparent text-primary border border-subtle hover:bg-hover',
        danger:
          'bg-tint-error text-tint-error-text border border-tint-error-border hover:brightness-95',
      },
      size: {
        sm: 'text-[12px] px-[14px] py-[6px] toque:min-h-10 rounded-wc-md',
        md: 'text-[14px] px-5 py-[10px] rounded-wc-md',
        lg: 'text-[16px] px-7 py-[14px] rounded-wc-lg',
      },
      iconOnly: {
        true: 'p-0 gap-0',
        false: '',
      },
    },
    compoundVariants: [
      { iconOnly: true, size: 'sm', class: 'size-8 toque:size-10' },
      { iconOnly: true, size: 'md', class: 'size-9' },
      { iconOnly: true, size: 'lg', class: 'size-11' },
    ],
    defaultVariants: { variant: 'primary', size: 'md', iconOnly: false },
  },
);

export interface ButtonProps
  extends
    Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'color'>,
    VariantProps<typeof button> {
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, iconOnly, loading, disabled, children, type, ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      disabled={disabled === true || loading === true}
      aria-busy={loading === true || undefined}
      className={cn(button({ variant, size, iconOnly }), className)}
      {...props}
    >
      {loading === true && (
        <LoaderCircle
          size={16}
          strokeWidth={1.5}
          aria-hidden="true"
          className="animate-spin motion-reduce:animate-none"
        />
      )}
      {children}
    </button>
  );
});
